import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAddMember } from '@/hooks/api/useMembers';
import { OrganizationRole } from '@kirin/api-client/contracts/organizations';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Add member modal component
 * Creates an invitation for the user. User will be added to organization when they accept the invite.
 * If user doesn't exist, Descope will create them and send an invitation email.
 */
export const AddMemberModal = ({
  isOpen,
  onClose,
  organizationId,
  existingEmails = [],
}: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  // Lower-cased emails already in the org (active members + pending invites).
  // Used to block re-adding an existing user (KIR-4040): the backend silently
  // treats an existing invite as a re-invite that rotates the token, so the
  // client is the only place this requirement is actually enforced.
  existingEmails?: string[];
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrganizationRole>(OrganizationRole.Member);
  const [emailError, setEmailError] = useState<string | null>(null);
  const { mutateAsync: addMember, isPending: isAdding } = useAddMember();
  const { toast } = useToast();

  // Clear form when modal closes to ensure fresh state on next open
  const handleClose = () => {
    setEmail('');
    setRole(OrganizationRole.Member);
    setEmailError(null);
    onClose();
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Clear a stale duplicate error as soon as the admin edits the field
    if (emailError) setEmailError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Block re-adding an existing user (active member or pending invite). An
    // expired invite still counts — it persists in the list now (KIR-4040), so
    // Resend on their row is the single explicit path to revive it.
    const normalizedEmail = email.trim().toLowerCase();
    if (existingEmails.includes(normalizedEmail)) {
      setEmailError(
        'This user is already in the organization. If they have a pending invitation, use Resend on their row to send a new one.'
      );
      return;
    }

    try {
      await addMember({ organizationId, email: normalizedEmail, role });
      toast({
        title: 'Success',
        description: 'Invited successfully',
        variant: 'success',
      });
      handleClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add member';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Failed to add member:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Add Member</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={e => handleEmailChange(e.target.value)}
                placeholder="Enter email address"
                aria-invalid={emailError ? true : undefined}
                required
              />
              {emailError && (
                <p className="mt-1 text-sm text-destructive" role="alert">
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Role</label>
              <Select
                value={role}
                onValueChange={value => setRole(value as OrganizationRole)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OrganizationRole.Member}>
                    Member
                  </SelectItem>
                  <SelectItem value={OrganizationRole.Manager}>
                    Manager
                  </SelectItem>
                  <SelectItem value={OrganizationRole.Owner}>Owner</SelectItem>
                  <SelectItem value={OrganizationRole.Support}>
                    Support
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isAdding}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isAdding}>
                {isAdding ? 'Inviting...' : 'Invite Member'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
